import React, { useState } from "react";
import { Vibrate, Link2, Unlink, Power, PlugZap, Square, Play, Lock, Unlock, ChevronUp, ChevronDown, TestTube } from "lucide-react";
import { DEFAULT_INTIFACE_WS } from "@/lib/buttplug";
import { getPatternsByCategory } from "@/lib/vibrationPatterns";

function StatusPill({ ok, okText, offText }) {
  return (
    <span className={`inline-flex items-center gap-2 font-mono-data text-xs px-3 py-1.5 border ${
      ok ? "border-[var(--kink-purple)]/40 text-[var(--kink-purple)]" : "border-[var(--kink-overlay)] text-[var(--kink-muted)]"
    }`}>
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-[var(--kink-purple)] pulse-dot" : "bg-[var(--kink-muted)]"}`} />
      {ok ? okText : offText}
    </span>
  );
}

export function ToysPanel({ toys, locked = false, onToggleLock }) {
  const [url, setUrl] = useState(DEFAULT_INTIFACE_WS);
  const [shrunk, setShrunk] = useState(false);

  return (
    <div className="hud-panel p-5 sm:p-6 mb-6" data-testid="toys-panel">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display font-black uppercase tracking-[0.08em] text-lg flex items-center gap-2">
            <Vibrate size={18} className="text-[var(--kink-purple)]" /> Toys — Lovense & Other Bluetooth Toys
            <button
              type="button"
              onClick={() => setShrunk((s) => !s)}
              data-testid="toys-panel-shrink-button"
              title={shrunk ? "Expand panel" : "Shrink panel"}
              aria-label={shrunk ? "Expand toy control panel" : "Shrink toy control panel"}
              className="ml-1 shrink-0 p-1 text-[var(--kink-muted)] hover:text-[var(--kink-purple)] transition-colors"
            >
              {shrunk ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </h2>
          {!shrunk && (
            <>
              <p className="text-[var(--kink-text-2)] text-sm mt-1">
                Connects to <span className="font-mono-data">Intiface Central</span> running on this machine —
                a free local app that holds the Bluetooth link to Lovense and dozens of other toy brands.{" "}
                <a href="https://intiface.com" target="_blank" rel="noreferrer" className="underline hover:text-[var(--kink-purple)]">
                  Get it here
                </a>, then press "Start Server" in it before connecting below. Works standalone — no OSSM required —
                or synced to the OSSM's SPEED via the toggle once connected.
              </p>
              <p className="text-[var(--kink-text-2)] text-sm mt-2">
                <span className="font-mono-data text-xs uppercase tracking-wide text-[var(--kink-muted)]">MuSe / Love Spouse toys</span> —
                these don't speak Bluetooth GATT (they only listen for BLE broadcasts), so neither a browser nor Intiface
                can talk to one directly. A cheap ESP32 flashed with an open-source gateway firmware (e.g.{" "}
                <a href="https://github.com/Fi0nee/LS-Buttplug" target="_blank" rel="noreferrer" className="underline hover:text-[var(--kink-purple)]">
                  LS-Buttplug
                </a>{" "}
                or{" "}
                <a href="https://github.com/IngeniousKink/LVS-Gateway" target="_blank" rel="noreferrer" className="underline hover:text-[var(--kink-purple)]">
                  LVS-Gateway
                </a>
                ) sits in between: it broadcasts the Love Spouse commands on one side and shows up in Intiface Central as
                a regular Lovense toy on the other. Once that's flashed and Intiface sees it, it works here exactly like
                any other toy below — no separate setup in this app.
              </p>
            </>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <StatusPill ok={toys.connected} okText="INTIFACE ONLINE" offText="INTIFACE OFFLINE" />
            {toys.isVirtual && (
              <span
                data-testid="toys-virtual-badge"
                className="inline-flex items-center gap-1.5 font-mono-data text-[10px] tracking-[0.15em] px-2 py-1 border border-[var(--kink-purple)]/50 text-[var(--kink-purple)]"
              >
                <TestTube size={11} /> VIRTUAL TEST DEVICE
              </span>
            )}
            {toys.connected && (
              <span className="font-mono-data text-xs text-[var(--kink-muted)]" data-testid="toys-found-count">
                {toys.devices.length} toy{toys.devices.length === 1 ? "" : "s"} found
              </span>
            )}
            {locked && (
              <span
                data-testid="toys-locked-badge"
                className="inline-flex items-center gap-1.5 font-mono-data text-[10px] tracking-[0.15em] px-2 py-1 border border-[var(--kink-danger)] text-[var(--kink-danger)]"
              >
                <Lock size={11} /> GUESTS LOCKED OUT
              </span>
            )}
          </div>
          {!shrunk && !toys.connected && (
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mt-3 bg-transparent border border-[var(--kink-overlay)] px-3 py-2 font-mono-data text-xs w-64 focus:outline-none focus:border-[var(--kink-purple)]/40"
              placeholder={DEFAULT_INTIFACE_WS}
              data-testid="intiface-url-input"
            />
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {toys.connected ? (
            <>
              {onToggleLock && (
                <button
                  onClick={onToggleLock}
                  data-testid="toys-kill-switch"
                  className={`flex items-center gap-2 border px-4 py-3 font-display text-xs tracking-[0.1em] transition-colors ${
                    locked
                      ? "border-[var(--kink-danger)] text-[var(--kink-danger)]"
                      : "border-[var(--kink-overlay)] text-[var(--kink-text-2)] hover:border-[var(--kink-danger)]/60 hover:text-[var(--kink-danger)]"
                  }`}
                >
                  {locked ? <Unlock size={16} /> : <Lock size={16} />}{" "}
                  {locked ? "UNLOCK GUEST TOYS" : "LOCK GUEST TOYS"}
                </button>
              )}
              <button
                onClick={() => toys.setLinked(!toys.linked)}
                data-testid="toys-link-toggle"
                className={`flex items-center gap-2 border px-4 py-3 font-display text-xs tracking-[0.1em] transition-colors ${
                  toys.linked
                    ? "border-[var(--kink-purple)]/50 text-[var(--kink-purple)]"
                    : "border-[var(--kink-overlay)] text-[var(--kink-text-2)] hover:border-[var(--kink-purple)]/40"
                }`}
              >
                {toys.linked ? <Link2 size={16} /> : <Unlink size={16} />} {toys.linked ? "LINKED TO SPEED" : "MANUAL"}
              </button>
              <button
                onClick={toys.stopAllToys}
                data-testid="toys-stop-button"
                className="flex items-center gap-2 bg-[var(--kink-danger)] text-white px-4 py-3 font-display font-bold tracking-[0.1em] active:scale-95 transition-transform"
              >
                <Power size={16} /> STOP TOYS
              </button>
              <button
                onClick={toys.disconnect}
                data-testid="toys-disconnect-button"
                className="flex items-center gap-2 border border-[var(--kink-overlay)] px-4 py-3 font-display text-xs tracking-[0.1em] hover:border-[var(--kink-danger)]/50 hover:text-[var(--kink-danger)] transition-colors"
              >
                DISCONNECT
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => toys.connect(url)}
                data-testid="toys-connect-button"
                className="flex items-center gap-2 bg-[var(--kink-purple)] text-[var(--kink-base)] px-5 py-3 font-display font-bold tracking-[0.1em] glow-purple active:scale-95 transition-transform"
              >
                <PlugZap size={16} /> CONNECT TOYS
              </button>
              <button
                onClick={toys.connectVirtual}
                data-testid="toys-connect-virtual-button"
                title="Connect an in-app software test device (Virtual Lovense + Virtual OSSM) — no hardware needed"
                className="flex items-center gap-2 border border-[var(--kink-overlay)] px-4 py-3 font-display text-xs tracking-[0.1em] text-[var(--kink-text-2)] hover:border-[var(--kink-purple)]/40 transition-colors"
              >
                <TestTube size={16} /> TEST DEVICE
              </button>
            </>
          )}
        </div>
      </div>

      {!shrunk && toys.connected && toys.devices.length === 0 && (
        <p className="font-mono-data text-xs text-[var(--kink-muted)] mt-4" data-testid="toys-empty-note">
          No toys found yet — put your device in pairing mode near this computer. Intiface scans automatically.
        </p>
      )}

      {!shrunk && toys.connected && toys.devices.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[var(--kink-overlay)]">
          <div className="flex items-center justify-between gap-3 mb-3">
            <span className="font-mono-data text-xs uppercase tracking-wide text-[var(--kink-muted)]">
              Vibration Patterns
            </span>
            {toys.activePattern && (
              <button
                onClick={toys.stopPattern}
                data-testid="toys-pattern-stop-button"
                className="flex items-center gap-1.5 border border-[var(--kink-overlay)] px-3 py-1.5 font-mono-data text-xs hover:border-[var(--kink-danger)]/50 hover:text-[var(--kink-danger)] transition-colors"
              >
                <Square size={12} /> STOP PATTERN
              </button>
            )}
          </div>
          <div className="space-y-3" data-testid="toys-pattern-list">
            {getPatternsByCategory().map((cat) => (
              <div key={cat.key}>
                <span className="font-mono-data text-[10px] uppercase tracking-wide text-[var(--kink-muted)] block mb-1.5">
                  {cat.label}
                </span>
                <div className="flex flex-wrap gap-2">
                  {cat.patterns.map((p) => {
                    const active = toys.activePattern === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => toys.startPattern(p.id)}
                        data-testid={`toys-pattern-${p.id}`}
                        title={p.description}
                        className={`flex items-center gap-1.5 border px-3 py-2 font-display text-xs tracking-[0.08em] transition-colors ${
                          active
                            ? "border-[var(--kink-purple)]/60 text-[var(--kink-purple)] glow-purple"
                            : "border-[var(--kink-overlay)] text-[var(--kink-text-2)] hover:border-[var(--kink-purple)]/40"
                        }`}
                      >
                        {active ? <Vibrate size={12} className="pulse-dot" /> : <Play size={12} />} {p.label.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {toys.linked && (
            <p className="font-mono-data text-[11px] text-[var(--kink-muted)] mt-2">
              Starting a pattern switches toys out of LINKED TO SPEED and into manual mode.
            </p>
          )}
        </div>
      )}

      {!shrunk && toys.connected && toys.devices.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[var(--kink-overlay)] space-y-4">
          {toys.devices.map((d) => (
            <div key={d.index} className="flex items-center justify-between gap-4" data-testid={`toy-row-${d.index}`}>
              <span className="font-mono-data text-sm text-[var(--kink-text-2)] truncate">{d.name}</span>
              {toys.isVirtual && (
                <div className="flex items-center gap-2 flex-1 min-w-0" data-testid={`toy-virtual-level-${d.index}`}>
                  <div className="h-2 flex-1 min-w-0 bg-[var(--kink-base)] border border-[var(--kink-overlay)] overflow-hidden">
                    <div
                      className="h-full bg-[var(--kink-purple)] transition-[width] duration-100"
                      style={{ width: `${Math.round((toys.virtualLevels[d.index] || 0) * 100)}%` }}
                    />
                  </div>
                  <span className="font-mono-data text-[10px] tabular-nums text-[var(--kink-muted)] w-9 text-right shrink-0">
                    {Math.round((toys.virtualLevels[d.index] || 0) * 100)}%
                  </span>
                </div>
              )}
              {toys.linked ? (
                <span className="font-mono-data text-xs text-[var(--kink-purple)]">mirrors SPEED</span>
              ) : toys.activePattern ? (
                <span className="font-mono-data text-xs text-[var(--kink-purple)]">
                  running {toys.activePattern}
                </span>
              ) : (
                <input
                  type="range"
                  min={0}
                  max={100}
                  defaultValue={0}
                  onChange={(e) => toys.setDeviceIntensity(d.index, Number(e.target.value) / 100)}
                  className="w-40 accent-[var(--kink-purple)]"
                  data-testid={`toy-slider-${d.index}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
